import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';

import DateField from 'src/components/DateField/DateField';
import InputText from 'src/components/InputText/InputText';
import SelectField from 'src/components/SelectField/SelectField';

import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { CHILD_RELATIONS_OPTIONS } from '@/const/relations';
import { getGuardianRelationshipLabel } from '@/logics/guardian/guardian';
import { UpdateChildFormData } from '@/pages/mypage';
import { Child } from '@/types/child';

type Props = {
  child: Child | undefined;
  errors: FieldErrors<UpdateChildFormData>;
  control: Control<UpdateChildFormData>;
  register: UseFormRegister<UpdateChildFormData>;
};

const BasicInfoForm = ({ child, errors, control, register }: Props) => {
  return (
    <div className="space-y-4 p-4">
      <div className="space-y-4">
        <div className="text-base font-bold">管理番号</div>
        <div className="text-base">{child?.id}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用契約開始日</div>
        <div className="text-base">{child ? child.contract_start_date : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用契約終了日</div>
        <div className="text-base">{child ? child.contract_end_date : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用者（児童）（姓）</div>
        <div className="text-base">{child ? child.last_name : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用者（児童）（名）</div>
        <div className="text-base">{child ? child.first_name : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用者（児童）（セイ）</div>
        <div className="text-base">{child ? child.last_name_kana : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">利用者（児童）（メイ）</div>
        <div className="text-base">{child ? child.first_name_kana : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <DateField
          label="生年月日"
          errors={errors.birthDate}
          control={control}
          name="birthDate"
        />
        <hr className="border-gray-400" />
      </div>

      {child?.guardians.map((guardian, index) => (
        <div
          className="space-y-4"
          key={index}
        >
          <div className="space-y-4">
            <div className="text-base font-bold">保護者_{index + 1}</div>
            <div className="text-base">{guardian.last_name + guardian.first_name}</div>
            <hr className="border-gray-400" />
          </div>

          <div className="space-y-4">
            <div className="text-base font-bold">保護者_{index + 1}（関係性）</div>
            <div className="text-base">{getGuardianRelationshipLabel(guardian.relation_ship)}</div>
            <hr className="border-gray-400" />
          </div>
        </div>
      ))}

      <div className="space-y-4">
        <div className="text-base font-bold">メールアドレス</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="email"
          errors={errors.email}
          {...register('email', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">連絡先TEL</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="tel"
          errors={errors.phoneNumber}
          {...register('phoneNumber', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">郵便番号</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="postalCode"
          errors={errors.postalCode}
          {...register('postalCode', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">都道府県</div>
        <SelectField
          name="prefecture"
          control={control}
          items={PREFECTURE_OPTIONS.map((prefecture) => ({
            label: prefecture.name,
            value: prefecture.name,
          }))}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">市区町村</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="text"
          errors={errors.city}
          {...register('city', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">住所1</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="text"
          errors={errors.address1}
          {...register('address1', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">住所2</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="text"
          {...register('address2')}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">緊急連絡先（関係性）</div>
        <SelectField
          name="emergencyContactRelation"
          control={control}
          items={CHILD_RELATIONS_OPTIONS}
          required={false}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">緊急連絡先（TEL）</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="tel"
          errors={errors.emergencyContactPhone}
          {...register('emergencyContactPhone', { required: true })}
        />
        <hr className="border-gray-400" />
      </div>
    </div>
  );
};

export default BasicInfoForm;
